import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import jsPDF from "jspdf";

const EUR_TO_KMF = 491.96;
const fmtKMF = (v) => Math.round(parseFloat(v||0)).toLocaleString("fr-FR") + " KMF";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", {day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "-";

export default function ReportsPage() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("month");
  const [eventId, setEventId] = useState("");
  const [merchantId, setMerchantId] = useState("");
  const [merchants, setMerchants] = useState([]);
  const [events, setEvents] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("ventes");

  useEffect(() => {
    api.get("/events").then(r => setEvents(r.data)).catch(() => {});
    api.get("/admin/merchants").then(r => setMerchants(r.data)).catch(() => {});
    loadReport();
  }, []);

  const loadReport = async (p, e, m) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("period", p !== undefined ? p : period);
      if (e !== undefined ? e : eventId) params.set("event_id", e !== undefined ? e : eventId);
      if (m !== undefined ? m : merchantId) params.set("merchant_id", m !== undefined ? m : merchantId);
      const res = await api.get("/reports/data?" + params.toString());
      setData(res.data);
    } catch(err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleFilter = (p, e, m) => {
    if (p !== undefined) setPeriod(p);
    if (e !== undefined) setEventId(e);
    if (m !== undefined) setMerchantId(m);
    loadReport(p !== undefined ? p : period, e !== undefined ? e : eventId, m !== undefined ? m : merchantId);
  };

  const exportCSV = (rows, filename, headers, fields) => {
    const bom = "\uFEFF";
    const csv = bom + [headers.join(";"), ...rows.map(r => fields.map(f => {
      const v = r[f] !== undefined ? r[f] : "";
      return '"' + String(v).replace(/"/g, '""') + '"';
    }).join(";"))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename + ".csv"; a.click();
  };

  const exportPDF = (title, headers, rows, fields) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    doc.setFillColor(37, 99, 235);
    doc.rect(0, 0, 297, 20, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("ComoTicket - " + title, 10, 13);
    doc.setFontSize(9);
    doc.text("Genere le " + new Date().toLocaleDateString("fr-FR"), 250, 13, {align: "right"});

    let y = 28;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setFillColor(240, 240, 240);
    doc.rect(8, y-5, 281, 7, "F");
    const colWidth = 280 / headers.length;
    headers.forEach((h, i) => doc.text(h, 10 + i * colWidth, y));
    y += 5;
    doc.setFont("helvetica", "normal");

    rows.forEach((row, ri) => {
      if (y > 185) { doc.addPage(); y = 15; }
      if (ri % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(8, y-4, 281, 6, "F"); }
      fields.forEach((f, i) => {
        const v = row[f] !== undefined ? String(row[f]) : "";
        doc.text(v.substring(0, 20), 10 + i * colWidth, y);
      });
      y += 6;
    });

    doc.save(title.replace(/ /g, "_") + ".pdf");
  };

  const periodLabel = { day: "Aujourd'hui", week: "7 derniers jours", month: "30 derniers jours", all: "Tout" };

  return (
    <div style={{minHeight:"100vh",background:"var(--gray-50)",fontFamily:"'Inter',Arial,sans-serif"}}>
      <div style={{background:"white",borderBottom:"1px solid var(--gray-200)",padding:"16px 32px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <a href={user && user.role === "admin" ? "/admin" : "/merchant"} style={{color:"var(--blue)",textDecoration:"none",fontSize:13}}>&larr; Retour</a>
          <h1 style={{fontSize:20,fontWeight:800,color:"var(--navy)"}}>&#128202; Rapports comptables</h1>
        </div>
        <div style={{fontSize:13,color:"var(--gray-500)"}}>
          <span dangerouslySetInnerHTML={{__html: (user ? user.name : "") + (data ? " &mdash; G&eacute;n&eacute;r&eacute; le " + fmtDate(data.generated_at) : "")}} />
        </div>
      </div>

      <div style={{padding:"24px 32px"}}>
        <div style={{background:"white",borderRadius:12,padding:20,marginBottom:20,border:"1px solid var(--gray-200)",display:"flex",gap:16,flexWrap:"wrap",alignItems:"flex-end"}}>
          <div>
            <label style={{fontSize:12,fontWeight:600,color:"var(--gray-500)",display:"block",marginBottom:6}}>P&eacute;riode</label>
            <div style={{display:"flex",gap:8}}>
              {["day","week","month","all"].map(p => (
                <button key={p} onClick={() => handleFilter(p, undefined, undefined)} style={{padding:"6px 14px",borderRadius:8,border:"1.5px solid " + (period===p?"var(--blue)":"var(--gray-200)"),background:period===p?"#eff6ff":"white",color:period===p?"var(--blue)":"var(--gray-600)",fontWeight:period===p?700:400,fontSize:13,cursor:"pointer"}}>
                  {p==="day"?"Jour":p==="week"?"Semaine":p==="month"?"Mois":"Tout"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={{fontSize:12,fontWeight:600,color:"var(--gray-500)",display:"block",marginBottom:6}}>&Eacute;v&eacute;nement</label>
            <select style={{padding:"8px 12px",borderRadius:8,border:"1.5px solid var(--gray-200)",fontSize:13,background:"white",color:"var(--gray-700)"}} value={eventId} onChange={e => handleFilter(undefined, e.target.value, undefined)}>
              <option value="">Tous les &eacute;v&eacute;nements</option>
              {events.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          {user && user.role === "admin" && (
            <div>
              <label style={{fontSize:12,fontWeight:600,color:"var(--gray-500)",display:"block",marginBottom:6}}>Commer&ccedil;ant</label>
              <select style={{padding:"8px 12px",borderRadius:8,border:"1.5px solid var(--gray-200)",fontSize:13,background:"white",color:"var(--gray-700)"}} value={merchantId} onChange={e => handleFilter(undefined, undefined, e.target.value)}>
                <option value="">Tous les commer&ccedil;ants</option>
                {merchants.map(m => <option key={m.id} value={m.id}>{m.name}{m.shop_name ? " - " + m.shop_name : ""}</option>)}
              </select>
            </div>
          )}
          <button onClick={() => loadReport(period, eventId, merchantId)} style={{padding:"8px 20px",background:"var(--blue)",color:"white",border:"none",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer"}}>
            &#128260; Actualiser
          </button>
        </div>

        {data && (
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:20}}>
            {[
              {label:"Billets vendus",value:data.totals.total_ventes,icon:"&#127915;",color:"#2563eb"},
              {label:"Chiffre d affaires",value:Math.round(data.totals.total_ca_kmf).toLocaleString("fr-FR")+" KMF",icon:"&#128176;",color:"#10b981"},
              {label:"Commissions dues",value:Math.round(data.totals.total_comm_due_kmf).toLocaleString("fr-FR")+" KMF",icon:"&#9203;",color:"#f59e0b"},
              {label:"Commissions pay&eacute;es",value:Math.round(data.totals.total_comm_paid_kmf).toLocaleString("fr-FR")+" KMF",icon:"&#10003;",color:"#10b981"},
            ].map((s,i) => (
              <div key={i} style={{background:"white",borderRadius:12,padding:16,border:"1px solid var(--gray-200)"}}>
                <div style={{fontSize:24,marginBottom:8}} dangerouslySetInnerHTML={{__html:s.icon}} />
                <div style={{fontSize:12,color:"var(--gray-500)",marginBottom:4}} dangerouslySetInnerHTML={{__html:s.label}} />
                <div style={{fontSize:18,fontWeight:800,color:s.color}}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{background:"white",borderRadius:12,border:"1px solid var(--gray-200)"}}>
          <div style={{display:"flex",borderBottom:"1px solid var(--gray-200)",padding:"0 20px"}}>
            {["ventes","commissions","paiements","annulations"].map(t => (
              <button key={t} onClick={() => setActiveTab(t)} style={{padding:"14px 20px",border:"none",borderBottom:"2px solid " + (activeTab===t?"var(--blue)":"transparent"),background:"none",fontWeight:activeTab===t?700:400,color:activeTab===t?"var(--blue)":"var(--gray-500)",fontSize:13,cursor:"pointer",textTransform:"capitalize"}}>
                {t === "ventes" ? "Ventes" : t === "commissions" ? "Commissions" : t === "paiements" ? "Paiements" : "Annulations"}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{padding:40,textAlign:"center",color:"var(--gray-400)"}}>Chargement...</div>
          ) : data && (
            <div style={{padding:20}}>
              {activeTab === "ventes" && (
                <>
                  <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginBottom:16}}>
                    <button onClick={() => exportCSV(data.sales, "ventes", ["R&eacute;f&eacute;rence","Date","Commer&ccedil;ant","&Eacute;v&eacute;nement","Cat&eacute;gorie","Porteur","Total KMF","Commission KMF","Pay&eacute;"], ["sale_reference","sale_date","merchant_name","event_name","category_name","holder_name","total_kmf","commission_kmf","commission_paid"])} style={{padding:"6px 16px",background:"#10b981",color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>&#128190; Exporter CSV</button>
                    <button onClick={() => exportPDF("Rapport des ventes", ["Ref","Date","Commercant","Evenement","Categorie","Total KMF","Comm KMF"], data.sales, ["sale_reference","sale_date","merchant_name","event_name","category_name","total_kmf","commission_kmf"])} style={{padding:"6px 16px",background:"#ef4444",color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>&#128196; Exporter PDF</button>
                  </div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                      <thead><tr style={{background:"var(--gray-50)"}}>
                        <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:"var(--gray-600)",borderBottom:"1px solid var(--gray-200)"}}>R&eacute;f&eacute;rence</th>
                        <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:"var(--gray-600)",borderBottom:"1px solid var(--gray-200)"}}>Date</th>
                        <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:"var(--gray-600)",borderBottom:"1px solid var(--gray-200)"}}>Commer&ccedil;ant</th>
                        <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:"var(--gray-600)",borderBottom:"1px solid var(--gray-200)"}}>&Eacute;v&eacute;nement</th>
                        <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:"var(--gray-600)",borderBottom:"1px solid var(--gray-200)"}}>Cat&eacute;gorie</th>
                        <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:"var(--gray-600)",borderBottom:"1px solid var(--gray-200)"}}>Porteur</th>
                        <th style={{padding:"8px 12px",textAlign:"right",fontWeight:600,color:"var(--gray-600)",borderBottom:"1px solid var(--gray-200)"}}>Total KMF</th>
                        <th style={{padding:"8px 12px",textAlign:"right",fontWeight:600,color:"var(--gray-600)",borderBottom:"1px solid var(--gray-200)"}}>Commission</th>
                        <th style={{padding:"8px 12px",textAlign:"center",fontWeight:600,color:"var(--gray-600)",borderBottom:"1px solid var(--gray-200)"}}>Statut</th>
                      </tr></thead>
                      <tbody>
                        {data.sales.length === 0 ? <tr><td colSpan={9} style={{textAlign:"center",padding:32,color:"var(--gray-400)"}}>Aucune vente sur cette p&eacute;riode</td></tr>
                        : data.sales.map((s,i) => (
                          <tr key={i} style={{borderBottom:"1px solid var(--gray-100)",background:i%2===0?"white":"var(--gray-50)"}}>
                            <td style={{padding:"8px 12px",fontFamily:"monospace",fontSize:12}}>{s.sale_reference}</td>
                            <td style={{padding:"8px 12px",fontSize:12}}>{fmtDate(s.sale_date)}</td>
                            <td style={{padding:"8px 12px",fontWeight:600}}>{s.merchant_name}</td>
                            <td style={{padding:"8px 12px"}}>{s.event_name}</td>
                            <td style={{padding:"8px 12px"}}>{s.category_name}</td>
                            <td style={{padding:"8px 12px"}}>{s.holder_name || "-"}</td>
                            <td style={{padding:"8px 12px",textAlign:"right",fontWeight:700,color:"#2563eb"}}>{parseInt(s.total_kmf).toLocaleString("fr-FR")} KMF</td>
                            <td style={{padding:"8px 12px",textAlign:"right",color:"#10b981"}}>{parseInt(s.commission_kmf).toLocaleString("fr-FR")} KMF</td>
                            <td style={{padding:"8px 12px",textAlign:"center"}}><span style={{background:s.commission_paid?"#d1fae5":"#fef3c7",color:s.commission_paid?"#065f46":"#92400e",padding:"2px 8px",borderRadius:100,fontSize:11,fontWeight:700}}><span dangerouslySetInnerHTML={{__html: s.commission_paid ? "Pay&eacute;" : "En attente"}} /></span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {activeTab === "commissions" && (
                <>
                  <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginBottom:16}}>
                    <button onClick={() => exportCSV(data.commissions, "commissions", ["Commer&ccedil;ant","Magasin","&Eacute;v&eacute;nement","Nb ventes","CA KMF","Commission KMF","Pay&eacute; KMF","Due KMF"], ["merchant_name","shop_name","event_name","nb_ventes","ca_kmf","comm_kmf","comm_paid_kmf","comm_due_kmf"])} style={{padding:"6px 16px",background:"#10b981",color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>&#128190; Exporter CSV</button>
                    <button onClick={() => exportPDF("Rapport commissions", ["Commercant","Evenement","Nb ventes","CA KMF","Comm KMF","Due KMF"], data.commissions, ["merchant_name","event_name","nb_ventes","ca_kmf","comm_kmf","comm_due_kmf"])} style={{padding:"6px 16px",background:"#ef4444",color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>&#128196; Exporter PDF</button>
                  </div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                      <thead><tr style={{background:"var(--gray-50)"}}>
                        {["Commer&ccedil;ant","Magasin","&Eacute;v&eacute;nement","Nb ventes","CA KMF","Commission","Pay&eacute;","Due"].map((h,i) => (
                          <th key={i} style={{padding:"8px 12px",textAlign:i>2?"right":"left",fontWeight:600,color:"var(--gray-600)",borderBottom:"1px solid var(--gray-200)"}} dangerouslySetInnerHTML={{__html:h}} />
                        ))}
                      </tr></thead>
                      <tbody>
                        {data.commissions.length === 0 ? <tr><td colSpan={8} style={{textAlign:"center",padding:32,color:"var(--gray-400)"}}>Aucune donn&eacute;e</td></tr>
                        : data.commissions.map((c,i) => (
                          <tr key={i} style={{borderBottom:"1px solid var(--gray-100)",background:i%2===0?"white":"var(--gray-50)"}}>
                            <td style={{padding:"8px 12px",fontWeight:600}}>{c.merchant_name}</td>
                            <td style={{padding:"8px 12px"}}>{c.shop_name || "-"}</td>
                            <td style={{padding:"8px 12px"}}>{c.event_name}</td>
                            <td style={{padding:"8px 12px",textAlign:"right"}}>{c.nb_ventes}</td>
                            <td style={{padding:"8px 12px",textAlign:"right",fontWeight:700}}>{parseInt(c.ca_kmf).toLocaleString("fr-FR")} KMF</td>
                            <td style={{padding:"8px 12px",textAlign:"right",color:"#10b981",fontWeight:700}}>{parseInt(c.comm_kmf).toLocaleString("fr-FR")} KMF</td>
                            <td style={{padding:"8px 12px",textAlign:"right",color:"#2563eb",fontWeight:700}}>{parseInt(c.comm_paid_kmf).toLocaleString("fr-FR")} KMF</td>
                            <td style={{padding:"8px 12px",textAlign:"right",color:"#f59e0b",fontWeight:700}}>{parseInt(c.comm_due_kmf).toLocaleString("fr-FR")} KMF</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {activeTab === "paiements" && (
                <>
                  <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginBottom:16}}>
                    <button onClick={() => exportCSV(data.payments, "paiements", ["Date paiement","Date enreg.","Commer&ccedil;ant","&Eacute;v&eacute;nement","Montant KMF","Pay&eacute; par","Note"], ["payment_date","created_at","merchant_name","event_name","amount_kmf","paid_by","note"])} style={{padding:"6px 16px",background:"#10b981",color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>&#128190; Exporter CSV</button>
                    <button onClick={() => exportPDF("Rapport paiements", ["Date paiement","Commercant","Evenement","Montant KMF","Paye par","Note"], data.payments, ["payment_date","merchant_name","event_name","amount_kmf","paid_by","note"])} style={{padding:"6px 16px",background:"#ef4444",color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>&#128196; Exporter PDF</button>
                  </div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                      <thead><tr style={{background:"var(--gray-50)"}}>
                        {["Date paiement","Date enreg.","Commer&ccedil;ant","Magasin","&Eacute;v&eacute;nement","Montant","Pay&eacute; par","Note"].map((h,i) => (
                          <th key={i} style={{padding:"8px 12px",textAlign:i===5?"right":"left",fontWeight:600,color:"var(--gray-600)",borderBottom:"1px solid var(--gray-200)"}} dangerouslySetInnerHTML={{__html:h}} />
                        ))}
                      </tr></thead>
                      <tbody>
                        {data.payments.length === 0 ? <tr><td colSpan={8} style={{textAlign:"center",padding:32,color:"var(--gray-400)"}}>Aucun paiement</td></tr>
                        : data.payments.map((p,i) => (
                          <tr key={i} style={{borderBottom:"1px solid var(--gray-100)",background:i%2===0?"white":"var(--gray-50)"}}>
                            <td style={{padding:"8px 12px",fontSize:12,fontWeight:600}}>{fmtDate(p.payment_date)}</td>
                            <td style={{padding:"8px 12px",fontSize:11,color:"var(--gray-400)"}}>{fmtDate(p.created_at)}</td>
                            <td style={{padding:"8px 12px",fontWeight:600}}>{p.merchant_name}</td>
                            <td style={{padding:"8px 12px"}}>{p.shop_name || "-"}</td>
                            <td style={{padding:"8px 12px"}}>{p.event_name || "Toutes ventes"}</td>
                            <td style={{padding:"8px 12px",textAlign:"right",fontWeight:700,color:"#10b981"}}>{parseInt(p.amount_kmf).toLocaleString("fr-FR")} KMF</td>
                            <td style={{padding:"8px 12px"}}>{p.paid_by}</td>
                            <td style={{padding:"8px 12px",fontSize:12,color:"var(--gray-500)"}}>{p.note || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {activeTab === "annulations" && (
                <>
                  <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginBottom:16}}>
                    <button onClick={() => exportCSV(data.cancelled, "annulations", ["R&eacute;f&eacute;rence","Date annulation","Commer&ccedil;ant","&Eacute;v&eacute;nement","Cat&eacute;gorie","Prix KMF","Commission KMF","Raison","Annul&eacute; par"], ["sale_reference","cancelled_at","merchant_name","event_name","category_name","price_kmf","commission_kmf","reason","cancelled_by_name"])} style={{padding:"6px 16px",background:"#10b981",color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>&#128190; Exporter CSV</button>
                    <button onClick={() => exportPDF("Rapport annulations", ["Ref","Date","Commercant","Evenement","Categorie","Prix KMF","Comm KMF","Raison"], data.cancelled, ["sale_reference","cancelled_at","merchant_name","event_name","category_name","price_kmf","commission_kmf","reason"])} style={{padding:"6px 16px",background:"#ef4444",color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>&#128196; Exporter PDF</button>
                  </div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                      <thead><tr style={{background:"var(--gray-50)"}}>
                        {["R&eacute;f&eacute;rence","Date annulation","Commer&ccedil;ant","Magasin","&Eacute;v&eacute;nement","Cat&eacute;gorie","Prix","Commission","Raison","Annul&eacute; par"].map((h,i) => (
                          <th key={i} style={{padding:"8px 12px",textAlign:i===6||i===7?"right":"left",fontWeight:600,color:"var(--gray-600)",borderBottom:"1px solid var(--gray-200)"}} dangerouslySetInnerHTML={{__html:h}} />
                        ))}
                      </tr></thead>
                      <tbody>
                        {data.cancelled.length === 0 ? <tr><td colSpan={10} style={{textAlign:"center",padding:32,color:"var(--gray-400)"}}>Aucune annulation sur cette p&eacute;riode</td></tr>
                        : data.cancelled.map((c,i) => (
                          <tr key={i} style={{borderBottom:"1px solid var(--gray-100)",background:i%2===0?"white":"var(--gray-50)"}}>
                            <td style={{padding:"8px 12px",fontFamily:"monospace",fontSize:12}}>{c.sale_reference}</td>
                            <td style={{padding:"8px 12px",fontSize:12}}>{fmtDate(c.cancelled_at)}</td>
                            <td style={{padding:"8px 12px",fontWeight:600}}>{c.merchant_name}</td>
                            <td style={{padding:"8px 12px"}}>{c.shop_name || "-"}</td>
                            <td style={{padding:"8px 12px"}}>{c.event_name}</td>
                            <td style={{padding:"8px 12px"}}>{c.category_name}</td>
                            <td style={{padding:"8px 12px",textAlign:"right",fontWeight:700,color:"#2563eb"}}>{parseInt(c.price_kmf).toLocaleString("fr-FR")} KMF</td>
                            <td style={{padding:"8px 12px",textAlign:"right",color:"#ef4444"}}>{parseInt(c.commission_kmf).toLocaleString("fr-FR")} KMF</td>
                            <td style={{padding:"8px 12px",fontSize:12,color:"var(--gray-500)"}}>{c.reason || "-"}</td>
                            <td style={{padding:"8px 12px",fontSize:12}}>{c.cancelled_by_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}